import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { tokenManager } from '../../services/api';
import axios from 'axios';
import './OpinionPage.css';

const OPINION_API_URL = process.env.REACT_APP_OPINION_API_URL || 'http://localhost:8002';

function OpinionPage() {
  const { isAuthenticated, user, refreshUser, isAdmin, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [content, setContent] = useState('');
  const [images, setImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  // 检查登录状态
  useEffect(() => {
    // 等待认证初始化完成
    if (loading) return;
    
    if (!isAuthenticated || !user) {
      navigate('/login', { state: { from: location }, replace: true });
    }
  }, [loading, isAuthenticated, user, navigate, location]);

  // JWT 过期检查
  const isJwtExpired = (token) => {
    try {
      const parts = String(token).split('.');
      if (parts.length !== 3) return false;
      const base64Url = parts[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64.padEnd(base64.length + (4 - (base64.length % 4)) % 4, '=');
      const payload = JSON.parse(decodeURIComponent(escape(window.atob(padded))));
      if (!payload || typeof payload.exp !== 'number') return false;
      const nowSeconds = Math.floor(Date.now() / 1000);
      return payload.exp <= nowSeconds;
    } catch (e) {
      return false;
    }
  };

  const ensureAuthenticated = async () => {
    if (!isAuthenticated || !user) {
      setError('请先登录后再使用该功能');
      navigate('/login', { state: { from: location }, replace: true });
      return false;
    }

    const accessToken = tokenManager.getAccessToken();
    if (!accessToken) {
      setError('请先登录后再使用该功能');
      navigate('/login', { state: { from: location }, replace: true });
      return false;
    }

    // access token 过期时，尝试刷新
    if (isJwtExpired(accessToken)) {
      try {
        await refreshUser();
        return true;
      } catch (e) {
        setError('登录已过期，请重新登录后使用');
        navigate('/login', { state: { from: location }, replace: true });
        return false;
      }
    }

    return true;
  };

  // 处理文件选择
  const handleFileSelect = (files) => {
    const fileList = Array.from(files || []);
    const imageFiles = fileList.filter(file => file.type.startsWith('image/'));
    
    if (imageFiles.length === 0) {
      setError('请选择图片文件');
      return;
    }

    // 限制最多 5 张图片
    const newImages = [...images, ...imageFiles].slice(0, 5);
    setImages(newImages);

    // 创建预览
    const newPreviews = [];
    newImages.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        newPreviews.push(e.target.result);
        if (newPreviews.length === newImages.length) {
          setImagePreviews([...newPreviews]);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const handleFileInputChange = (e) => {
    handleFileSelect(e.target.files);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // 拖拽处理
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
    handleFileSelect(e.dataTransfer.files);
  };

  // 移除图片
  const handleRemoveImage = (index) => {
    const newImages = images.filter((_, i) => i !== index);
    const newPreviews = imagePreviews.filter((_, i) => i !== index);
    setImages(newImages);
    setImagePreviews(newPreviews);
  };

  // 再次提交
  const handleSubmitAgain = () => {
    setSuccess(false);
    setError('');
    setContent('');
    setImages([]);
    setImagePreviews([]);
  };

  // 提交意见
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const ok = await ensureAuthenticated();
    if (!ok) return;

    if (!content.trim()) {
      setError('请输入意见内容');
      return;
    }

    setIsSubmitting(true);
    setError('');
    setSuccess(false);

    try {
      const formData = new FormData();
      formData.append('content', content.trim());
      formData.append('username', user.username || user.user_id || '');
      formData.append('email', user.email || '');

      // 添加图片
      images.forEach((image) => {
        formData.append('images', image);
      });

      const accessToken = tokenManager.getAccessToken();
      const response = await axios.post(`${OPINION_API_URL}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      // 提交成功
      setSuccess(true);
      setContent('');
      setImages([]);
      setImagePreviews([]);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 
        err.response?.data?.message || 
        err.message || 
        '提交失败，请稍后重试';
      setError(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return <div className="opinion-page"><div className="opinion-container"><p>加载中...</p></div></div>;
  }

  if (!isAuthenticated || !user) {
    return null; // 等待重定向
  }

  // 成功页面
  if (success) {
    return (
      <div className="opinion-page">
        <div className="opinion-container success-container">
          <div className="success-content">
            <div className="success-icon-wrapper">
              <div className="success-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                  <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>
            <h1 className="success-title">提交成功！</h1>
            <p className="success-message-text">
              感谢您的宝贵意见，我们会认真对待每一条反馈
            </p>
            <p className="success-subtitle">
              您的意见已成功提交，我们会尽快处理
            </p>
            <button
              type="button"
              className="submit-again-button"
              onClick={handleSubmitAgain}
            >
              <span className="button-icon">➕</span>
              再次提交
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 表单页面
  return (
    <div className="opinion-page">
      <div className="opinion-container">
        <div className="opinion-header">
          <div className="header-top">
            <h1>意见反馈</h1>
            {isAdmin && (
              <Link to="/management/opinions" className="admin-link">
                🔧 管理意见
              </Link>
            )}
          </div>
          <p>我们非常重视您的意见和建议，请详细描述您的问题或建议</p>
        </div>

        <form onSubmit={handleSubmit} className="opinion-form">
          {error && (
            <div className="error-message">
              ⚠️ {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="content">
              <span className="label-icon">💭</span>
              意见内容 <span className="required">*</span>
            </label>
            <textarea
              id="content"
              ref={textareaRef}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="请详细描述您的意见、建议或遇到的问题..."
              rows={8}
              required
            />
            <div className="char-count">
              {content.length} 字
            </div>
          </div>

          <div className="form-group">
            <label>
              <span className="label-icon">📷</span>
              上传图片 <span className="optional">（可选，最多5张）</span>
            </label>
            <div
              className={`upload-area ${isDragActive ? 'drag-active' : ''} ${images.length >= 5 ? 'disabled' : ''}`}
              onDragEnter={handleDragEnter}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileInputChange}
                disabled={images.length >= 5}
                style={{ display: 'none' }}
              />
              <button
                type="button"
                className="upload-button"
                onClick={() => fileInputRef.current?.click()}
                disabled={images.length >= 5}
              >
                <span className="upload-icon">📤</span>
                {images.length >= 5 ? '已达到最大上传数量' : '点击或拖拽上传图片'}
              </button>
              <p className="upload-hint">支持 JPG、PNG 格式，单张不超过 10MB</p>
            </div>

            {imagePreviews.length > 0 && (
              <div className="image-previews">
                {imagePreviews.map((preview, index) => (
                  <div key={index} className="image-preview-item">
                    <img src={preview} alt={`预览 ${index + 1}`} />
                    <button
                      type="button"
                      className="remove-image-btn"
                      onClick={() => handleRemoveImage(index)}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="form-actions">
            <button
              type="submit"
              className="submit-button"
              disabled={isSubmitting || !content.trim()}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner"></span>
                  提交中...
                </>
              ) : (
                <>
                  <span className="submit-icon">✓</span>
                  提交意见
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default OpinionPage;

